import React from 'react';
import {Box, Checkbox, FormControlLabel, FormGroup, Stack, Switch, TextField, Typography} from '@mui/material';
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import {UploadMediaQuestion} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface UploadMediaInputProps {
    question: UploadMediaQuestion;
    onQuestionUpdate: (question: UploadMediaQuestion) => void;
    isSelected?: boolean;
    onSelect?: () => void;
}

const UploadMediaInput: React.FC<UploadMediaInputProps> = ({
                                                               question,
                                                               onQuestionUpdate,
                                                               isSelected = false,
                                                               onSelect
                                                           }) => {
    const {t} = useTranslation();

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            title: e.target.value
        });
    };

    const handleDescriptionChange = (value?: string) => {
        onQuestionUpdate({
            ...question,
            description: value || ''
        });
    };

    const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            required: e.target.checked
        });
    };

    const handleAllowMultipleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            allowMultiple: e.target.checked
        });
    };

    const handleAllowedTypesChange = (type: 'image' | 'video' | 'file') => {
        const currentTypes = question.allowedTypes || ['image', 'video', 'file'];
        let newTypes: ('image' | 'video' | 'file')[];

        if (currentTypes.includes(type)) {
            newTypes = currentTypes.filter(t => t !== type);
        } else {
            newTypes = [...currentTypes, type];
        }

        // Ensure at least one type is selected
        if (newTypes.length === 0) {
            newTypes = [type];
        }

        onQuestionUpdate({
            ...question,
            allowedTypes: newTypes
        });
    };

    const allowedTypes = question.allowedTypes || ['image', 'video', 'file'];

    return (
        <Stack
            onClick={onSelect}
            sx={{
                p: 3,
                borderRadius: 1,
                border: isSelected ? '2px solid' : '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: 'primary.light',
                    backgroundColor: 'action.hover'
                },
                position: 'relative'
            }}
        >
            <Box sx={{alignSelf: 'flex-end'}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={handleRequiredChange}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                        />
                    }
                    label={<Typography variant="caption">{t('Required')}</Typography>}
                    labelPlacement="start"
                    onClick={(e) => e.stopPropagation()}
                    sx={{m: 0}}
                />
            </Box>

            <TextField
                fullWidth
                label={t('Question Label')}
                value={question.title}
                onChange={handleTitleChange}
                variant="standard"
                sx={{mb: 2, pr: 12}}
                onClick={(e) => e.stopPropagation()}
            />

            {isSelected ? (
                <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
                    <MdEditor
                        value={question.description || ''}
                        onChange={handleDescriptionChange}
                        placeholder={t('Question Description (optional)')}
                        className={'mini-mdx'}
                    />
                </Box>
            ) : (
                question.description && (
                    <Box sx={{mb: 2}}>
                        <MdRenderer>
                            {question.description}
                        </MdRenderer>
                    </Box>
                )
            )}

            {isSelected && (
                <>
                    <Box sx={{mb: 2}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={question.allowMultiple || false}
                                    onChange={handleAllowMultipleChange}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            }
                            label={t('Allow multiple file uploads')}
                        />
                    </Box>

                    <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                            {t('Allowed File Types')}
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={allowedTypes.includes('image')}
                                        onChange={() => handleAllowedTypesChange('image')}
                                    />
                                }
                                label={t('Images')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={allowedTypes.includes('video')}
                                        onChange={() => handleAllowedTypesChange('video')}
                                    />
                                }
                                label={t('Videos')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={allowedTypes.includes('file')}
                                        onChange={() => handleAllowedTypesChange('file')}
                                    />
                                }
                                label={t('Files/Documents')}
                            />
                        </FormGroup>
                    </Box>
                </>
            )}
        </Stack>
    );
};

export default UploadMediaInput;
